import { type IssueDetail, ISSUE_TEXT_MAX_LENGTH } from '@cortex/domain-ac-mens-hub-api';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  formatShortDate,
  ISSUE_EFFORT_LABEL_KEYS,
  ISSUE_REJECT_REASON_LABEL_KEYS,
} from '../../lib/issue-mgmt-format.js';
import { InlineTextEditor } from './InlineTextEditor.js';
import { IssueHistory } from './IssueHistory.js';
import { IssueThemeEditor } from './IssueThemeEditor.js';
import { MonthField } from './MonthField.js';
import type { SaveIssueFields } from './useIssueFieldSave.js';

/**
 * 工数の目安の並び。
 * @graph-connects none
 */
const EFFORTS = ['small', 'medium', 'large'] as const;

/**
 * 選択欄の見た目（Hub の入力欄にそろえる）。
 * @graph-connects none
 */
const SELECT_CLASS =
  'rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-1.5 text-sm text-[var(--color-text-primary)] disabled:opacity-60';

/**
 * 「その他」の1行（左に項目名、右に値と操作）。
 * @graph-connects none
 */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 border-b border-[var(--color-border)] px-4 py-3 last:border-b-0 md:grid-cols-[10rem_1fr] md:gap-4">
      <div className="text-xs font-semibold text-[var(--color-text-secondary)]">{label}</div>
      <div className="min-w-0 text-sm text-[var(--color-text-primary)]">{children}</div>
    </div>
  );
}

/**
 * 課題詳細の「その他」（畳んで出す）。理由・テーマ・領域・目標時期・開発するか・工数の目安・旧番号・関連リンク・メモ・
 * 統合・移行時の説明・対応の記録・変更履歴。編集キーの人はテーマ・領域・目標時期・工数・関連リンク・メモを直せ、
 * 別の課題にまとめられる。開発するかと理由は「判定を変える」で直す。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の補足の項目（理由・テーマ・領域・目標時期・統合・移行時の説明・変更履歴など）を畳んで見せ、編集キーの人がその場で直せるようにする
 * @graph-connects IssueThemeEditor [calls] テーマ
 * @graph-connects InlineTextEditor [calls] 関連リンクとメモ
 * @graph-connects IssueHistory [calls] 変更履歴
 * @graph-connects MonthField [calls] 目標時期
 */
export function IssueOthers({
  issue,
  canEdit,
  save,
  domainOptions,
  onMerge,
  now,
}: {
  issue: IssueDetail;
  canEdit: boolean;
  save: SaveIssueFields;
  domainOptions: readonly string[];
  onMerge: () => void;
  now: Date;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dash = t('issueMgmt.common.dash');
  const fieldSaved = (fieldKey: string) =>
    t('issueMgmt.others.fieldSaved', { title: issue.title, field: t(fieldKey) });
  const domains = [
    ...new Set([...domainOptions, ...(issue.domain === null ? [] : [issue.domain])]),
  ].sort((a, b) => a.localeCompare(b, 'ja'));

  const reason =
    issue.verdict === 'rejected' && issue.rejectReason !== null
      ? t(ISSUE_REJECT_REASON_LABEL_KEYS[issue.rejectReason])
      : issue.verdict === 'on_hold' && issue.holdUntil !== null
        ? t('issueMgmt.detail.holdUntil', { date: formatShortDate(issue.holdUntil, now) })
        : null;

  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-[var(--color-text-secondary)]"
      >
        {open ? (
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        )}
        {t('issueMgmt.others.heading')}
      </button>
      {open && (
        <div className="border-t border-[var(--color-border)]">
          <Row label={t('issueMgmt.others.reason')}>
            {reason ?? dash}
            {issue.verdictNote !== null && (
              <p className="mt-1 whitespace-pre-wrap text-xs text-[var(--color-text-secondary)]">
                {issue.verdictNote}
              </p>
            )}
          </Row>
          <Row label={t('issueMgmt.others.themes')}>
            <IssueThemeEditor issue={issue} canEdit={canEdit} save={save} />
          </Row>
          <Row label={t('issueMgmt.others.domain')}>
            <select
              aria-label={t('issueMgmt.others.domain')}
              className={SELECT_CLASS}
              disabled={!canEdit}
              value={issue.domain ?? ''}
              onChange={(e) => {
                void save(
                  [
                    {
                      field: 'domain',
                      base: issue.domain,
                      value: e.target.value === '' ? null : e.target.value,
                    },
                  ],
                  fieldSaved('issueMgmt.field.domain'),
                );
              }}
            >
              <option value="">{t('issueMgmt.others.noneSelected')}</option>
              {domains.map((domain) => (
                <option key={domain} value={domain}>
                  {domain}
                </option>
              ))}
            </select>
          </Row>
          <Row label={t('issueMgmt.others.targetMonth')}>
            <MonthField
              label={t('issueMgmt.others.targetMonth')}
              className={SELECT_CLASS}
              disabled={!canEdit}
              value={issue.targetMonth}
              onSave={(next, base) => {
                void save(
                  [{ field: 'targetMonth', base, value: next }],
                  fieldSaved('issueMgmt.field.targetMonth'),
                );
              }}
            />
          </Row>
          <Row label={t('issueMgmt.others.devRequired')}>
            {issue.devRequired === null
              ? dash
              : issue.devRequired
                ? t('issueMgmt.detail.devYes')
                : t('issueMgmt.detail.devNo')}
          </Row>
          <Row label={t('issueMgmt.others.effort')}>
            <select
              aria-label={t('issueMgmt.others.effort')}
              className={SELECT_CLASS}
              disabled={!canEdit}
              value={issue.effortEstimate ?? ''}
              onChange={(e) => {
                const value = EFFORTS.find((effort) => effort === e.target.value) ?? null;
                void save(
                  [{ field: 'effortEstimate', base: issue.effortEstimate, value }],
                  fieldSaved('issueMgmt.field.effort'),
                );
              }}
            >
              <option value="">{t('issueMgmt.others.noneSelected')}</option>
              {EFFORTS.map((effort) => (
                <option key={effort} value={effort}>
                  {t(ISSUE_EFFORT_LABEL_KEYS[effort])}
                </option>
              ))}
            </select>
          </Row>
          <Row label={t('issueMgmt.others.legacyIds')}>
            {issue.legacyIds.length === 0 ? dash : issue.legacyIds.join('・')}
          </Row>
          <Row label={t('issueMgmt.others.links')}>
            <InlineTextEditor
              label={t('issueMgmt.others.links')}
              value={issue.relatedLinks.length === 0 ? null : issue.relatedLinks.join('\n')}
              max={ISSUE_TEXT_MAX_LENGTH}
              canEdit={canEdit}
              onSave={(next) =>
                save(
                  [
                    {
                      field: 'relatedLinks',
                      base: issue.relatedLinks,
                      value: (next ?? '')
                        .split('\n')
                        .map((line) => line.trim())
                        .filter((line) => line !== ''),
                    },
                  ],
                  fieldSaved('issueMgmt.field.links'),
                )
              }
            >
              {issue.relatedLinks.length === 0 ? (
                <span className="text-[var(--color-text-muted)]">{dash}</span>
              ) : (
                <ul className="space-y-0.5">
                  {issue.relatedLinks.map((link) => (
                    <li key={link} className="break-all text-[var(--color-accent-primary)]">
                      {link}
                    </li>
                  ))}
                </ul>
              )}
            </InlineTextEditor>
            {canEdit && (
              <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                {t('issueMgmt.others.linksHint')}
              </p>
            )}
          </Row>
          <Row label={t('issueMgmt.others.memo')}>
            <InlineTextEditor
              label={t('issueMgmt.others.memo')}
              value={issue.memo}
              max={ISSUE_TEXT_MAX_LENGTH}
              canEdit={canEdit}
              onSave={(next) =>
                save(
                  [{ field: 'memo', base: issue.memo, value: next }],
                  fieldSaved('issueMgmt.field.memo'),
                )
              }
            >
              <p className="whitespace-pre-wrap">{issue.memo ?? dash}</p>
            </InlineTextEditor>
          </Row>
          <Row label={t('issueMgmt.others.merge')}>
            {issue.mergedFrom.length > 0 && (
              <div className="mb-2">
                <p className="text-xs text-[var(--color-text-secondary)]">
                  {t('issueMgmt.others.mergedFrom')}
                </p>
                <ul>
                  {issue.mergedFrom.map((merged) => (
                    <li key={merged.id}>
                      <Link
                        to="/issue-mgmt/issues/$issueId"
                        params={{ issueId: merged.id }}
                        className="text-[var(--color-accent-primary)] hover:underline"
                      >
                        <span className="mr-1 text-xs text-[var(--color-text-muted)]">
                          {merged.id}
                        </span>
                        {merged.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {canEdit ? (
              <button
                type="button"
                onClick={onMerge}
                className="text-xs font-medium text-[var(--color-accent-primary)] hover:underline"
              >
                {t('issueMgmt.others.mergeAction')}
              </button>
            ) : (
              issue.mergedFrom.length === 0 && dash
            )}
          </Row>
          <Row label={t('issueMgmt.others.migrationNote')}>
            <p className="whitespace-pre-wrap">{issue.migrationNote ?? dash}</p>
          </Row>
          {issue.doneNote !== null && (
            <Row label={t('issueMgmt.others.doneNote')}>
              <p className="whitespace-pre-wrap">{issue.doneNote}</p>
            </Row>
          )}
          <Row label={t('issueMgmt.others.history')}>
            <IssueHistory issueId={issue.id} now={now} />
          </Row>
        </div>
      )}
    </section>
  );
}
